import React, { useMemo } from "react";
import { useNavigate, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { ButtonType } from "@companion-core/shared/app/Components/Button/button.model";
import { Button } from "@companion-core/shared/app/Components/Button";
import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import { EventLink } from "@companion-core/shared/app/Components/EventLink";
import { AccountMenu } from "@companion-core/web/src/Components/AccountMenu";
import { NavLinkItem } from "@companion-core/shared/app/Interfaces/navigation";
import { useAuthStore } from "@companion-core/shared/app/Store/authStore";
import { useAccountNavigationStore } from "@companion-core/shared/app/Store/accountNavigationStore";
import { useConfigStore } from "@companion-core/shared/app/Store/configStore";
import { useHeaderStore } from "@companion-core/shared/app/Store/header";
import { isDarkColor } from "@companion-core/shared/app/Utils/colors";
import { useHeaderScroll } from "@companion-core/web/src/Hooks/headerScroll";
import { useWebNavigationHeader } from "@companion-core/web/src/Hooks/navigationHeader";
import {
  trackClickButtonSubscribe,
  trackClickButtonLogin,
} from "@companion-core/shared/app/tracking/home";
import { useMediaQuery } from "@companion-core/web/src/Utilities/mediaQuery";
import { isTabBarVisible } from "@companion-core/web/src/Utilities/tabBar";
import "Assets/Styles/scss/header.scss";

const LogoImg: React.FC<{ logoUri: string }> = ({ logoUri }) => (
  <img
    data-testid="header__logo"
    className="header__logo-img header__logo"
    src={logoUri}
    alt="logo"
  />
);

const ClickableLogo: React.FC<{
  logoUri: string;
  logoLink?: string;
}> = ({ logoUri, logoLink }) => {
  return (
    <EventLink
      data-testid="header__logo"
      name="logo"
      key="logo"
      type={logoLink ? "external" : "internal"}
      link={logoLink}
      path="/"
      targetBlank={false}>
      <LogoImg logoUri={logoUri} />
    </EventLink>
  );
};

const NonClickableLogo: React.FC<{ logoUri: string }> = ({ logoUri }) => (
  <span data-testid="header__logo" className="header__logo-container">
    <LogoImg logoUri={logoUri} />
  </span>
);

const Header: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { navigateBackward } = useWebNavigationHeader();
  const isMobileSize = useMediaQuery("(max-width: 768px)");
  const { authenticated } = useAuthStore((state) => ({ authenticated: state.authenticated }));
  const { openPopover, isPopoverOpen } = useAccountNavigationStore((state) => ({
    openPopover: state.openPopover,
    isPopoverOpen: state.isPopoverOpen,
  }));
  const {
    disabledHeader,
    transparentHeader,
    isOnATransparentHeaderPage,
    shouldHideOnScroll,
    isProfileMenuIconVisible,
    isNavBarVisible,
    isLogoClickable,
  } = useHeaderStore((state) => ({
    disabledHeader: state.disabledHeader,
    transparentHeader: state.transparentHeader,
    isOnATransparentHeaderPage: state.isOnATransparentHeaderPage,
    isProfileMenuIconVisible: state.isProfileMenuIconVisible,
    isNavBarVisible: state.isNavBarVisible,
    shouldHideOnScroll: state.shouldHideOnScroll,
    isLogoClickable: state.isLogoClickable,
  }));
  const { config, branding } = useConfigStore((state) => ({
    config: state.config,
    branding: state.branding,
  }));
  const logoAssets = useMemo(() => branding?.theme?.assets?.logo, [branding]);
  const options = useMemo(() => config?.options, [config]);
  const accountIcon = useMemo(() => options?.accountIcon, [options]);
  const logoUri = useMemo(
    () => (!transparentHeader ? logoAssets?.default : logoAssets?.negative),
    [logoAssets, transparentHeader]
  );
  const logoLink = useMemo(() => options?.headerLogoLink, [options]);
  const isRegisterPage = useMemo(() => location.pathname.includes("register"), [location]);
  const isLoginPage = useMemo(() => location.pathname.includes("login"), [location]);
  const displayBackButton = !isTabBarVisible(options);
  const displayLogoCentered = useMemo(
    () => isMobileSize && (displayBackButton || !isProfileMenuIconVisible),
    [displayBackButton, isMobileSize, isProfileMenuIconVisible]
  );
  const isLogoAlone = useMemo(
    () =>
      (isMobileSize && !isProfileMenuIconVisible && !displayBackButton) ||
      (!isMobileSize && !isNavBarVisible && !isProfileMenuIconVisible),
    [isMobileSize, isProfileMenuIconVisible, displayBackButton, isNavBarVisible]
  );
  const { isHeaderVisible } = useHeaderScroll(isTabBarVisible(options), shouldHideOnScroll);
  const isHeaderBackgroundDark = useMemo(() => {
    const headerBackground = branding?.theme?.colors?.["color-header-background-primary"];
    if (headerBackground) {
      return isDarkColor(headerBackground);
    }
  }, [branding]);

  const BackButton = () => (
    <div
      data-testid="header__back-button"
      className="header__back-button"
      onClick={() => navigateBackward()}>
      <FontIcon name="JP-Arrow-left-bold" size={24} className="header__back-button-icon" />
    </div>
  );

  const NavLink: React.FC = () => {
    // Dynamic mapping of header links in config file
    return options?.headerLinks.map(({ name, type, link, path }: NavLinkItem) => {
      // check if user is on this page
      const selected = location.pathname.startsWith(path ?? "");

      return (
        <EventLink
          data-testid={`header__link--${name}`}
          key={name}
          name={name}
          type={type}
          link={link}
          path={path}>
          <span
            className={`link cursor-pointer uppercase header__nav-bar-item ${selected ? "body-text-5" : "body-text-6"}`}>
            {t(`common.menu.links.${name}`)}
          </span>
        </EventLink>
      );
    });
  };

  const NavBar: React.FC<{ disabled?: boolean }> = (props: { disabled?: boolean }) => {
    return (
      !props.disabled && (
        <nav
          data-testid="header__link-bar"
          className="header__nav-bar centered-horizontally sm:hidden">
          <NavLink />
        </nav>
      )
    );
  };

  const ProfileButton: React.FC<{ disabled?: boolean }> = (props: { disabled?: boolean }) => {
    if (props.disabled) {
      return false;
    }

    const className = `header__profile uppercase ${displayLogoCentered ? "hidden" : ""} ${
      isOnATransparentHeaderPage ? "hidden-with-box" : ""
    }`;

    if (!isMobileSize) {
      return (
        <div data-testid="header__profile" className={className}>
          <AccountMenu />
        </div>
      );
    }

    return (
      <button
        type="button"
        data-testid="header__profile"
        aria-label={t(
          `common.accessibility.header.buttons.profile.label.${isPopoverOpen ? "open" : "closed"}`
        )}
        aria-live="polite"
        className={`${className} cursor-pointer`}
        onClick={() => {
          if (!isOnATransparentHeaderPage) {
            openPopover();
          }
        }}>
        <FontIcon name={accountIcon} size={24} aria-hidden="true" />
      </button>
    );
  };

  const registerButtonType = (): ButtonType => (isRegisterPage ? "ghost" : "primary");
  const loginButtonType = (): ButtonType => {
    if (isLoginPage) {
      return "ghost";
    }
    if (isRegisterPage) {
      return "primary";
    }
    return "secondary";
  };

  // Action button Register / Login, with conditional render if connected or not
  const AuthActionButtons: React.FC<{ disabled?: boolean }> = (props: { disabled?: boolean }) => {
    return (
      !props.disabled && (
        <div className="header__button-container sm:hidden">
          <Button
            testID="header__button-register"
            type={registerButtonType()}
            size="medium"
            label={t("common.header.buttons.register")}
            onClick={() => {
              trackClickButtonSubscribe();
              navigate("/register/member");
            }}
          />
          {/* Connection button */}
          <Button
            testID="header__button-login"
            type={loginButtonType()}
            size="medium"
            label={t("common.header.buttons.login")}
            onClick={() => {
              trackClickButtonLogin();
              navigate("/login");
            }}
          />
        </div>
      )
    );
  };

  return (
    !disabledHeader && (
      <div
        className={`header__container ${transparentHeader ? "header__container--transparent" : ""} ${
          isHeaderBackgroundDark ? "header__container--negative" : ""
        } ${!isHeaderVisible && isMobileSize ? "header__container--hidden" : ""} ${isLogoAlone ? "header__container--logo-alone" : ""}`}>
        {displayBackButton && isMobileSize && <BackButton />}
        <span
          className={`header__logo-wrapper ${
            displayLogoCentered ? "header__logo-wrapper--centered" : ""
          }`}>
          {isLogoClickable ? (
            <ClickableLogo logoUri={logoUri} logoLink={logoLink} />
          ) : (
            <NonClickableLogo logoUri={logoUri} />
          )}
        </span>
        <AuthActionButtons disabled={authenticated} />
        <NavBar disabled={!authenticated || !isNavBarVisible} />
        <ProfileButton disabled={(!isMobileSize && !authenticated) || !isProfileMenuIconVisible} />
      </div>
    )
  );
};

export default Header;
